import ContentHeader from "src/widgets/contentHeader/ContentHeader";
import css from './HelpPage.module.scss';
import { getIndorImageSrc, IndorHuges, IndorImages, LoginIndorIDModal } from "src/features";
import { useEffect, useRef, useState } from "react";
import { useCheckIndorIDAuthQuery, useLazyCheckIndorIDAuthQuery } from "src/api/rtk/indorsoftRu/slices/indorIDAuthApiSlice";
import { useGetUnreadTicketStatsQuery, useCheckSupportModuleStatusQuery } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";
import ReportAnErrorModal from "src/widgets/help/reportAnError/ReportAnErrorModal";
import RequestHelpModal from "src/widgets/help/requestHelp/RequestHelpModal";
import ToastMessageContainer from "src/widgets/toastMessageContainer/ToastMessageContainer";
import SmallLoader from "src/widgets/loaders/SmallLoader";
import { useNavigate } from "react-router-dom";
import { getItemWebStorage } from "src/features/webStorageSaver";

const indorRoadGeoportalLogoSrc = require('../../assets/images/favicon_indor_road.svg').default
const indorPowerGeoportalLogoSrc = require('../../assets/images/favicon_indor_power.svg').default
const pdfInstructionIconSrc = require('../../assets/images/icons/pdf_export_icon.svg').default
const supportTicketListRelativeUrl = "supportTickets"

type SupportButtonType = 'help' | 'error' | 'list';

export default function HelpPage() {
    const navigate = useNavigate();
    const currentConfig = getItemWebStorage("currentConfig") ?? {};
    const isSupportEnabledInConfig = currentConfig?.support?.enabled ?? false;
    const [loginModalOpen, setLoginModalOpen] = useState(false)
    const [isReportAnErrorModalOpen, setIsReportAnErrorModalOpen] = useState(false)
    const [isRequestHelpModalOpen, setIsRequestHelpModalOpen] = useState(false)
    const [checkAuth] = useLazyCheckIndorIDAuthQuery();
    const { data: authData, refetch: refetchAuth, isLoading: isCheckingAuth } = useCheckIndorIDAuthQuery(undefined, {
        skip: !isSupportEnabledInConfig,
        refetchOnMountOrArgChange: true, // Всегда проверяем авторизацию при монтировании компонента
    });

    // Проверка активности модуля поддержки
    const { data: supportModuleStatus, isLoading: isCheckingModuleStatus } = useCheckSupportModuleStatusQuery(undefined, {
        skip: !isSupportEnabledInConfig,
        refetchOnMountOrArgChange: true,
    });

    const { data: unreadStats } = useGetUnreadTicketStatsQuery(undefined, {
        skip: !isSupportEnabledInConfig || !authData?.authorized || !supportModuleStatus?.isActive, // Запрашиваем статистику только если пользователь авторизован и модуль активен
        pollingInterval: 45000,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });
    const chosenType = useRef<null | SupportButtonType>(null)

    const isAuthorized = authData?.authorized ?? false;
    const unreadCount = unreadStats?.unreadTicketsCount ?? 0;
    const isSupportModuleActive = supportModuleStatus?.isActive ?? true; // По умолчанию считаем активным, пока не проверили
    const restServerType = currentConfig.restServerType
    let isInfoSectionShown = restServerType === "IndorRoad"
    let titleLogoSrc = indorRoadGeoportalLogoSrc;
    let titleServerType = "Road";
    let description = "Геопортал автомобильных дорог";
    if (restServerType === "IndorPower") {
        titleLogoSrc = indorPowerGeoportalLogoSrc
        titleServerType = "Power"
        description = "Геопортал электрических сетей"
    }

    const currentYear = new Date().getFullYear();

    // Принудительно обновляем проверку авторизации при монтировании компонента
    useEffect(() => {
        if (!isSupportEnabledInConfig) return;
        refetchAuth();
    }, []); // Выполняем только при монтировании

    async function handleSupportButtonClick(type: SupportButtonType | null) {
        chosenType.current = type
        try {
            const res: any = await checkAuth().unwrap()
            if (res?.authorized) {
                if (type === "error") setIsReportAnErrorModalOpen(true);
                if (type === "help") setIsRequestHelpModalOpen(true);
                if (type === "list") navigate(supportTicketListRelativeUrl);
                return;
            }
        } catch (e) {
            // игнорируем и открываем модальное окно
        }
        setLoginModalOpen(true)
    }

    function handleLoginSuccess() {
        setLoginModalOpen(false);
        refetchAuth();
        handleSupportButtonClick(chosenType.current);
    }

    return (
        <>
            <ContentHeader title="Справка" />
            <div className={css.root}>
                <ToastMessageContainer />
                <header className={css.header}>
                    <img src={titleLogoSrc} alt={`Логотип Indor${titleServerType} Geoportal`} className={css.logo} />
                    <div>
                        <h1>Indor<strong>{titleServerType}</strong> <strong>Geoportal</strong></h1>
                        <span className={css.description}>{description}</span>
                    </div>
                </header>
                <main>
                    {isInfoSectionShown &&
                        <section className={css.section} aria-labelledby="info">
                            <h5 className={css.sectionHeader}>Информация</h5>
                            <a
                                href="https://indorsoft.ru/products/road/geoportal"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <article>
                                    <img src={getIndorImageSrc(IndorHuges.HelpInternet, 128, "IndorHuges")} alt="" className={css.icon} />
                                    <div>
                                        <h5 className={css.articleHeader}>Информация о продукте</h5>
                                        <span className={css.articleDescription}>Перейти на страницу программного продукта</span>
                                    </div>
                                </article>
                            </a>
                            <a
                                href="https://help.indorsoft.ru/IndorGeo/Content/A_Topics/Introduction.htm"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <article>
                                    <img src={getIndorImageSrc(IndorHuges.HelpOnlineHelp, 128, "IndorHuges")} alt="" className={css.icon} />
                                    <div>
                                        <h5 className={css.articleHeader}>Онлайн-справка</h5>
                                        <span className={css.articleDescription}>Получить справку по работе с программным продуктом</span>
                                    </div>
                                </article>
                            </a>
                            <a
                                href="https://indorsoft.ru/products/road/geoportal/doc/IndorRoadGeoportal-user-guide.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <article>
                                    <img src={pdfInstructionIconSrc} alt="" className={css.icon} />
                                    <div>
                                        <h5 className={css.articleHeader}>Скачать руководство пользователя</h5>
                                        <span className={css.articleDescription}>Подробная инструкция по работе с продуктом</span>
                                    </div>
                                </article>
                            </a>
                        </section>
                    }

                    {isSupportEnabledInConfig && (
                        <section className={`${css.section} ${css.supportSection}`} aria-labelledby="support">
                            <h2 className={css.sectionHeader}>Поддержка</h2>
                            {isCheckingModuleStatus ? (
                                <div className={css.supportStatusWrapper}>
                                    <SmallLoader text="Проверка доступности службы поддержки..." iconColor="#65AFDF" />
                                </div>
                            ) : !isSupportModuleActive ? (
                                <div className={css.supportWarningBox}>
                                    <div className={css.warningIcon}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2L2 22H22L12 2Z" fill="#F59E0B" stroke="#000" strokeWidth="1.5" />
                                            <path d="M12 8V14" stroke="#000" strokeWidth="2" strokeLinecap="round" />
                                            <circle cx="12" cy="17" r="1" fill="#000" />
                                        </svg>
                                    </div>
                                    <div className={css.warningContent}>
                                        <div className={css.warningTitle}>
                                            Служба тех. поддержки временно недоступна
                                        </div>
                                        <div className={css.warningText}>
                                            Модуль тех. поддержки не активен. Обратитесь к администратору системы.
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className={css.supportSectionWrapper}>

                                    <article onClick={() => handleSupportButtonClick("help")}>
                                        <img src={getIndorImageSrc(IndorHuges.HelpSendQuestion, 128, "IndorHuges")} alt="Запросить помощь" className={css.icon} />
                                        <div>
                                            <h5 className={css.articleHeader}>Запросить помощь</h5>
                                            <span className={css.articleDescription}>Связаться со службой технической поддержки по электронной почте</span>
                                        </div>
                                    </article>

                                    <article onClick={() => handleSupportButtonClick("error")}>
                                        <img src={getIndorImageSrc(IndorHuges.HelpSendBugs, 128, "IndorHuges")} alt="Сообщить об ошибке" className={css.icon} />
                                        <div>
                                            <h5 className={css.articleHeader}>Сообщить об ошибке</h5>
                                            <span className={css.articleDescription}>Передать в службу технической поддержки сообщение об ошибке</span>
                                        </div>
                                    </article>

                                    <article onClick={() => handleSupportButtonClick("list")}>
                                        <img src={getIndorImageSrc(IndorImages.InternetMail, 128, "IndorImages")} alt="Список обращений" className={css.icon} />
                                        <div className={css.articleContent}>
                                            <h5 className={css.articleHeader}>Список обращений</h5>
                                            <span className={css.articleDescription}>Все отправленные ранее обращения</span>
                                        </div>
                                        {isAuthorized && unreadCount > 0 && (
                                            <span className={css.unreadBadge}>{unreadCount}</span>
                                        )}
                                    </article>

                                    {(!isAuthorized || isCheckingAuth) && (
                                        <div className={css.authOverlay}>
                                            <div className={css.authOverlayContent}>
                                                {isCheckingAuth ? (
                                                    <div className={css.authLoaderWrapper}>
                                                        <SmallLoader text="Проверка авторизации..." iconColor="#65AFDF" />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <p className={css.authOverlayText}>Для доступа к модулю поддержки необходимо авторизоваться через IndorID</p>
                                                        <button
                                                            className={css.authButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setLoginModalOpen(true);
                                                            }}
                                                        >
                                                            Войти через IndorID
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    <section className={css.section} aria-labelledby="about">
                        <h2 className={css.sectionHeader}>Сведения о производителе</h2>
                        <a
                            href="https://indorsoft.ru"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <article>
                                <img src={getIndorImageSrc(IndorHuges.HelpIndorSoftSite, 128, "IndorHuges")} alt="" className={css.icon} />
                                <div>
                                    <h5 className={css.articleHeader}>Web-сайт ИндорСофт</h5>
                                    <span className={css.articleDescription}>Перейти на официальный сайт компании ИндорСофт</span>
                                </div>
                            </article>
                        </a>

                        <address className={css.address}>
                            <b>Производитель:</b> ООО&nbsp;«ИндорСофт», г. Томск<br />
                            <b>Телефон/факс:</b> +7 (3822) 650-450<br />
                            <b>Бесплатно по России:</b> 8-800-333-08-05<br />
                            <b>Электронная почта:</b> <a href="mailto:support@indorsoft.ru">support@indorsoft.ru</a>
                        </address>
                    </section>
                </main>
                <LoginIndorIDModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} onLogin={handleLoginSuccess} />
                <ReportAnErrorModal open={isReportAnErrorModalOpen} onClose={() => setIsReportAnErrorModalOpen(false)} />
                <RequestHelpModal open={isRequestHelpModalOpen} onClose={() => setIsRequestHelpModalOpen(false)} />
                <footer>
                    <small>2003–{currentYear} © ИндорСофт. Все права охраняются законом. <a href="https://indorsoft.ru/about/privacy_policy/">Политика конфиденциальности.</a>
                    </small>
                </footer>
            </div>
        </>
    )
}
